import React, { useEffect, useState, useRef, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, PlusIcon, CheckIcon, SearchIcon, ChevronLeftIcon } from 'lucide-react';
interface AuthenticationFiltersProps {
  deduplication: 'deduplicated' | 'raw';
  onDeduplicationChange: (value: 'deduplicated' | 'raw') => void;
  cardTypes: string[];
  onCardTypesChange: (types: string[]) => void;
  cardCountries: string[];
  onCardCountriesChange: (countries: string[]) => void;
  cardBrands: string[];
  onCardBrandsChange: (brands: string[]) => void;
  interactionTypes: string[];
  onInteractionTypesChange: (types: string[]) => void;
  additionalFilters: string[];
  onAdditionalFiltersChange: (filters: string[]) => void;
}
type MoreFiltersView = 'menu' | 'currency' | 'retry-status';
export function AuthenticationFilters({
  deduplication,
  onDeduplicationChange,
  cardTypes,
  onCardTypesChange,
  cardCountries,
  onCardCountriesChange,
  cardBrands,
  onCardBrandsChange,
  interactionTypes,
  onInteractionTypesChange,
  additionalFilters,
  onAdditionalFiltersChange
}: AuthenticationFiltersProps) {
  const {
    t
  } = useTranslation('analytics');
  const [showDeduplicationDropdown, setShowDeduplicationDropdown] = useState(false);
  const [showCardTypeDropdown, setShowCardTypeDropdown] = useState(false);
  const [showCardCountryDropdown, setShowCardCountryDropdown] = useState(false);
  const [showCardBrandDropdown, setShowCardBrandDropdown] = useState(false);
  const [showInteractionTypeDropdown, setShowInteractionTypeDropdown] = useState(false);
  const [showMoreFiltersDropdown, setShowMoreFiltersDropdown] = useState(false);
  const [moreFiltersView, setMoreFiltersView] = useState<MoreFiltersView>('menu');
  // Shared payment filters (internal state)
  const [paymentMethodCategories, setPaymentMethodCategories] = useState<string[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [fundingSourceTypes, setFundingSourceTypes] = useState<string[]>([]);
  const [showPMCategoryDropdown, setShowPMCategoryDropdown] = useState(false);
  const [showPMMethodDropdown, setShowPMMethodDropdown] = useState(false);
  const [showFundingSourceDropdown, setShowFundingSourceDropdown] = useState(false);
  // Additional filter states
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [retryStatuses, setRetryStatuses] = useState<string[]>([]);
  const deduplicationRef = useRef<HTMLDivElement>(null);
  const cardTypeRef = useRef<HTMLDivElement>(null);
  const cardCountryRef = useRef<HTMLDivElement>(null);
  const cardBrandRef = useRef<HTMLDivElement>(null);
  const interactionTypeRef = useRef<HTMLDivElement>(null);
  const moreFiltersRef = useRef<HTMLDivElement>(null);
  // Shared payment filter refs
  const pmCategoryRef = useRef<HTMLDivElement>(null);
  const pmMethodRef = useRef<HTMLDivElement>(null);
  const fundingSourceRef = useRef<HTMLDivElement>(null);
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (deduplicationRef.current && !deduplicationRef.current.contains(event.target as Node)) {
        setShowDeduplicationDropdown(false);
      }
      if (cardTypeRef.current && !cardTypeRef.current.contains(event.target as Node)) {
        setShowCardTypeDropdown(false);
      }
      if (cardCountryRef.current && !cardCountryRef.current.contains(event.target as Node)) {
        setShowCardCountryDropdown(false);
      }
      if (cardBrandRef.current && !cardBrandRef.current.contains(event.target as Node)) {
        setShowCardBrandDropdown(false);
      }
      if (interactionTypeRef.current && !interactionTypeRef.current.contains(event.target as Node)) {
        setShowInteractionTypeDropdown(false);
      }
      if (moreFiltersRef.current && !moreFiltersRef.current.contains(event.target as Node)) {
        setShowMoreFiltersDropdown(false);
        setMoreFiltersView('menu');
      }
      if (pmCategoryRef.current && !pmCategoryRef.current.contains(event.target as Node)) {
        setShowPMCategoryDropdown(false);
      }
      if (pmMethodRef.current && !pmMethodRef.current.contains(event.target as Node)) {
        setShowPMMethodDropdown(false);
      }
      if (fundingSourceRef.current && !fundingSourceRef.current.contains(event.target as Node)) {
        setShowFundingSourceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const availableAdditionalFilters = [{
    id: 'currency',
    label: 'Currency'
  }, {
    id: 'retry-status',
    label: 'Retry status'
  }];
  // Card countries with Malaysia first, then alphabetically
  const cardCountryOptions = ['Malaysia', 'Afghanistan', 'Åland Islands', 'Albania', 'Algeria', 'American Samoa', 'Andorra', 'Angola', 'Anguilla', 'Australia', 'Singapore', 'United Kingdom', 'United States', 'Malaysia'];
  // Currency options with MYR first, then alphabetically
  const currencyOptions = ['MYR - Malaysian Ringgit', 'AED - United Arab Emirates Dirham', 'AFN - Afghan Afghani', 'ALL - Albanian Lek', 'AMD - Armenian Dram', 'ANG - Netherlands Antillean Guilder', 'AOA - Angolan Kwanza', 'ARS - Argentine Peso', 'AUD - Australian Dollar', 'SGD - Singapore Dollar', 'USD - US Dollar', 'MYR - Malaysian Ringgit'];
  const handleMoreFilterClick = (filterId: string) => {
    setMoreFiltersView(filterId as MoreFiltersView);
  };
  const handleBackToMenu = () => {
    setMoreFiltersView('menu');
  };
  const handleApplyFilter = (applyFn: () => void) => {
    applyFn();
    setShowMoreFiltersDropdown(false);
    setMoreFiltersView('menu');
  };
  return <>
      {/* Main Filters */}
      <div className="relative" ref={deduplicationRef} data-id="element-1332">
        <button onClick={() => setShowDeduplicationDropdown(!showDeduplicationDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-1333">
          <span className="capitalize" data-id="element-1334">{deduplication}</span>
          <ChevronDownIcon size={16} data-id="element-1335" />
        </button>

        {showDeduplicationDropdown && <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50" data-id="element-1336">
            <button onClick={() => {
          onDeduplicationChange('deduplicated');
          setShowDeduplicationDropdown(false);
        }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${deduplication === 'deduplicated' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-1337">
              <div className="flex-1 min-w-0" data-id="element-1338">
                <div className="text-sm font-medium text-gray-900" data-id="element-1339">
                  {t('components.AcceptanceFilters.deduplicated')}
                </div>
                <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-1340">
                  {t('components.AcceptanceFilters.only_includes_the_final_outcome_of')}
                </div>
              </div>
              {deduplication === 'deduplicated' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1341" />}
            </button>

            <button onClick={() => {
          onDeduplicationChange('raw');
          setShowDeduplicationDropdown(false);
        }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${deduplication === 'raw' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-1342">
              <div className="flex-1 min-w-0" data-id="element-1343">
                <div className="text-sm font-medium text-gray-900" data-id="element-1344">
                  {t('components.AcceptanceFilters.raw')}
                </div>
                <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-1345">
                  {t('components.AcceptanceFilters.includes_all_payment_attempts_shows_repeat')}
                </div>
              </div>
              {deduplication === 'raw' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1346" />}
            </button>
          </div>}
      </div>

      {/* Secondary Filters */}
      <div className="w-full flex flex-wrap items-center gap-2" data-id="element-1347">
        {/* Payment method category */}
        <div className="relative" ref={pmCategoryRef} data-id="element-1348">
          <button onClick={() => setShowPMCategoryDropdown(!showPMCategoryDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1349">
            <PlusIcon size={14} data-id="element-1350" />
            <span data-id="element-1351">
              {t('components.AcceptanceFilters.payment_method_category')}
            </span>
            {paymentMethodCategories.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1352">
                {paymentMethodCategories.length}
              </span>}
          </button>
          {showPMCategoryDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_category')} options={['Cards', 'Wallets', 'Bank transfers', 'Buy now pay later', 'Cash-based', 'Direct debit']} selectedOptions={paymentMethodCategories} onApply={selected => {
          setPaymentMethodCategories(selected);
          setShowPMCategoryDropdown(false);
        }} onBack={null} data-id="element-1353" />}
        </div>

        {/* Payment method */}
        <div className="relative" ref={pmMethodRef} data-id="element-1354">
          <button onClick={() => setShowPMMethodDropdown(!showPMMethodDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1355">
            <PlusIcon size={14} data-id="element-1356" />
            <span data-id="element-1357">{t('components.AcceptanceFilters.payment_method')}</span>
            {paymentMethods.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1358">
                {paymentMethods.length}
              </span>}
          </button>
          {showPMMethodDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_payment_method')} options={['Card', 'Apple Pay', 'Google Pay', 'Alipay', 'WeChat Pay', 'GrabPay', 'LINE Pay', 'TrueMoney', 'PromptPay', 'PayPal']} selectedOptions={paymentMethods} onApply={selected => {
          setPaymentMethods(selected);
          setShowPMMethodDropdown(false);
        }} onBack={null} data-id="element-1359" />}
        </div>

        {/* Funding source type */}
        <div className="relative" ref={fundingSourceRef} data-id="element-1360">
          <button onClick={() => setShowFundingSourceDropdown(!showFundingSourceDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1361">
            <PlusIcon size={14} data-id="element-1362" />
            <span data-id="element-1363">{t('components.AcceptanceFilters.funding_source_type')}</span>
            {fundingSourceTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1364">
                {fundingSourceTypes.length}
              </span>}
          </button>
          {showFundingSourceDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_funding_source')} options={['Card', 'Bank account', 'Stored balance', 'BNPL credit']} selectedOptions={fundingSourceTypes} onApply={selected => {
          setFundingSourceTypes(selected);
          setShowFundingSourceDropdown(false);
        }} onBack={null} data-id="element-1365" />}
        </div>

        <div className="relative" ref={cardTypeRef} data-id="element-1366">
          <button onClick={() => setShowCardTypeDropdown(!showCardTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1367">
            <PlusIcon size={14} data-id="element-1368" />
            <span data-id="element-1369">{t('components.AcceptanceFilters.card_type')}</span>
          </button>

          {showCardTypeDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_card_type')} options={['Credit card', 'Debit card', 'Prepaid card']} selectedOptions={cardTypes} onApply={selected => {
          onCardTypesChange(selected);
          setShowCardTypeDropdown(false);
        }} onBack={null} data-id="element-1370" />}
        </div>

        <div className="relative" ref={cardCountryRef} data-id="element-1371">
          <button onClick={() => setShowCardCountryDropdown(!showCardCountryDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1372">
            <PlusIcon size={14} data-id="element-1373" />
            <span data-id="element-1374">{t('components.AcceptanceFilters.card_country')}</span>
          </button>

          {showCardCountryDropdown && <SearchableDropdown title={t('components.AcceptanceFilters.attrs.filter_by_card_country')} options={cardCountryOptions} selectedOptions={cardCountries} onApply={selected => {
          onCardCountriesChange(selected);
          setShowCardCountryDropdown(false);
        }} data-id="element-1375" />}
        </div>

        <div className="relative" ref={cardBrandRef} data-id="element-1376">
          <button onClick={() => setShowCardBrandDropdown(!showCardBrandDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1377">
            <PlusIcon size={14} data-id="element-1378" />
            <span data-id="element-1379">{t('components.AcceptanceFilters.card_brand')}</span>
          </button>

          {showCardBrandDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_card_brand')} options={['Visa', 'Mastercard', 'American Express', 'Cartes Bancaires', 'Discover', 'eftpos Australia', 'Girocard', 'JCB', 'UnionPay']} selectedOptions={cardBrands} onApply={selected => {
          onCardBrandsChange(selected);
          setShowCardBrandDropdown(false);
        }} onBack={null} data-id="element-1380" />}
        </div>

        <div className="relative" ref={interactionTypeRef} data-id="element-1381">
          <button onClick={() => setShowInteractionTypeDropdown(!showInteractionTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1382">
            <PlusIcon size={14} data-id="element-1383" />
            <span data-id="element-1384">{t('components.AcceptanceFilters.interaction_type')}</span>
          </button>

          {showInteractionTypeDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_interaction_type')} options={['One time', 'Saved card', 'Recurring initial', 'Recurring subsequent', 'Unscheduled']} selectedOptions={interactionTypes} onApply={selected => {
          onInteractionTypesChange(selected);
          setShowInteractionTypeDropdown(false);
        }} onBack={null} data-id="element-1385" />}
        </div>

        <div className="relative" ref={moreFiltersRef} data-id="element-1386">
          <button onClick={() => {
          setShowMoreFiltersDropdown(!showMoreFiltersDropdown);
          setMoreFiltersView('menu');
        }} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1387">
            <PlusIcon size={14} data-id="element-1388" />
            <span data-id="element-1389">{t('components.AcceptanceFilters.more_filters')}</span>
          </button>

          {showMoreFiltersDropdown && <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50" data-id="element-1390">
              {moreFiltersView === 'menu' && <>
                  {availableAdditionalFilters.map(filter => <button key={filter.id} onClick={() => handleMoreFilterClick(filter.id)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-1391">
                      <PlusIcon size={14} className="text-blue-600" data-id="element-1392" />
                      <span data-id="element-1393">{filter.label}</span>
                    </button>)}
                </>}

              {moreFiltersView === 'currency' && <SearchableDropdown title={t('components.AcceptanceFilters.attrs.filter_by_currency')} options={currencyOptions} selectedOptions={currencies} onApply={selected => handleApplyFilter(() => setCurrencies(selected))} onBack={handleBackToMenu} isInline data-id="element-1394" />}

              {moreFiltersView === 'retry-status' && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_retry_status')} options={['Initial attempt', 'Retry']} selectedOptions={retryStatuses} onApply={selected => handleApplyFilter(() => setRetryStatuses(selected))} onBack={handleBackToMenu} isInline data-id="element-1395" />}
            </div>}
        </div>
      </div>
    </>;
}
// Compact Dropdown Component (for checkbox lists)
interface CompactDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack: (() => void) | null;
  isInline?: boolean;
}
function CompactDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: CompactDropdownProps) {
  const {
    t
  } = useTranslation('analytics');
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3" data-id="element-1396">
        {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-1397">
            <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-1398" />
          </button>}
        <h3 className="text-base font-semibold text-gray-900" data-id="element-1399">{title}</h3>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-1400">
        {options.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer" data-id="element-1401">
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1402" />

            <span className="text-base text-gray-900" data-id="element-1403">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-1404">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2.5 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-1405">
          {t('components.content.apply')}
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-1406">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-1407">
      {content}
    </div>;
}
// Searchable Dropdown Component (for card country and currency)
interface SearchableDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack?: (() => void) | null;
  isInline?: boolean;
}
function SearchableDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: SearchableDropdownProps) {
  const {
    t
  } = useTranslation('analytics');
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredOptions = options.filter(option => option.toLowerCase().includes(searchQuery.toLowerCase()));
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200" data-id="element-1408">
        <div className="flex items-center gap-3 mb-2" data-id="element-1409">
          {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-1410">
              <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-1411" />
            </button>}
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-1412">{title}</h3>
        </div>
        <div className="relative" data-id="element-1413">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1414" />

          <input type="search" aria-label={t('components.content.attrs.search')} placeholder={t('components.content.attrs.search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1415" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-1416">
        {filteredOptions.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer" data-id="element-1417">
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1418" />

            <span className="text-sm text-gray-900" data-id="element-1419">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-1420">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-1421">
          {t('components.content.apply')}
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-1422">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-1423">
      {content}
    </div>;
}